import { useState, useEffect } from "react";
import { Users, Plus, X, Trash, User, Envelope, Lock, Eye, EyeSlash, ShieldCheck, ShieldSlash } from "@phosphor-icons/react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function formatDate(iso) {
  try { return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }); } catch { return iso; }
}

export default function AdminAdmins() {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [showPassword, setShowPassword] = useState(false);

  const [form, setForm] = useState({ name: "", email: "", password: "", allow_delete: true, show_leads: true });
  const [error, setError] = useState("");

  const fetchAdmins = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/admins`);
      const data = await res.json();
      setAdmins(Array.isArray(data) ? data : []);
    } catch { setAdmins([]); } finally { setLoading(false); }
  };

  useEffect(() => { fetchAdmins(); }, []);

  const resetForm = () => { setForm({ name: "", email: "", password: "", allow_delete: true, show_leads: true }); setError(""); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password.length < 6) { setError("Password must be at least 6 characters."); return; }
    setSubmitting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/admins`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.detail || "Failed to create admin."); return; }
      await fetchAdmins();
      resetForm();
      setDrawerOpen(false);
    } catch { setError("Something went wrong. Please try again."); } finally { setSubmitting(false); }
  };

  const handleDelete = async (id) => {
    setDeleteId(id);
    try {
      await fetch(`${BACKEND_URL}/api/admin/admins/${id}`, { method: "DELETE" });
      setAdmins((prev) => prev.filter((a) => a.id !== id));
    } finally { setDeleteId(null); }
  };

  const inputCls = "w-full px-3.5 py-2.5 rounded-lg border text-sm outline-none transition-colors";
  const inputStyle = { borderColor: "rgba(26,92,55,0.2)", backgroundColor: "#F7F5F0", color: "#1C211F" };
  const labelCls = "block text-xs font-medium mb-1.5 uppercase tracking-wider";

  return (
    <div className="relative">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h3 className="font-heading text-xl font-medium" style={{ color: "#1C211F" }}>Admins</h3>
          <p className="text-sm mt-0.5" style={{ color: "#4A5450" }}>{admins.length} admin{admins.length !== 1 ? "s" : ""}</p>
        </div>
        <button
          onClick={() => { resetForm(); setDrawerOpen(true); }}
          className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors"
          style={{ backgroundColor: "#1A5C37", color: "#F7F5F0" }}
          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#14482B")}
          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#1A5C37")}
        >
          <Plus size={16} weight="bold" /> Create Admin
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-6 h-6 border-2 rounded-full animate-spin" style={{ borderColor: "#1A5C37", borderTopColor: "transparent" }} />
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {admins.map((a) => (
            <div key={a.id} className="flex items-center gap-4 p-5 rounded-xl border" style={{ backgroundColor: "#fff", borderColor: "rgba(26,92,55,0.1)" }}>
              <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 text-sm font-semibold" style={{ backgroundColor: "rgba(26,92,55,0.1)", color: "#1A5C37" }}>
                {a.name?.[0]?.toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-sm font-semibold" style={{ color: "#1C211F" }}>{a.name}</p>
                  {a.is_superadmin && (
                    <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ backgroundColor: "rgba(26,92,55,0.1)", color: "#1A5C37" }}>Superadmin</span>
                  )}
                </div>
                <p className="text-xs mt-0.5" style={{ color: "#4A5450" }}>{a.email}</p>
                <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2">
                  <span className="flex items-center gap-1 text-xs" style={{ color: a.allow_delete ? "#1A5C37" : "#B85C38" }}>
                    {a.allow_delete ? <ShieldCheck size={12} weight="duotone" /> : <ShieldSlash size={12} weight="duotone" />}
                    {a.allow_delete ? "Can delete" : "No delete"}
                  </span>
                  <span className="flex items-center gap-1 text-xs" style={{ color: a.show_leads ? "#1A5C37" : "#7A8A84" }}>
                    {a.show_leads ? "Leads visible" : "Leads hidden"}
                  </span>
                </div>
              </div>
              <div className="text-xs flex-shrink-0" style={{ color: "#7A8A84" }}>{formatDate(a.created_at)}</div>
              {!a.is_superadmin && (
                <button
                  onClick={() => handleDelete(a.id)} disabled={deleteId === a.id}
                  className="flex-shrink-0 p-1.5 rounded-lg transition-colors" style={{ color: "#B85C38" }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "rgba(184,92,56,0.08)")}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                >
                  <Trash size={16} weight="duotone" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {drawerOpen && <div className="fixed inset-0 z-40" style={{ backgroundColor: "rgba(0,0,0,0.35)" }} onClick={() => setDrawerOpen(false)} />}

      <div
        className={`fixed top-0 right-0 h-full z-50 flex flex-col transition-transform duration-300 ${drawerOpen ? "translate-x-0" : "translate-x-full"}`}
        style={{ width: "min(440px, 100vw)", backgroundColor: "#fff", boxShadow: "-8px 0 32px rgba(0,0,0,0.12)" }}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b flex-shrink-0" style={{ borderColor: "rgba(26,92,55,0.1)" }}>
          <h3 className="font-heading text-lg font-medium" style={{ color: "#1C211F" }}>Create Admin</h3>
          <button onClick={() => setDrawerOpen(false)} className="p-1.5 rounded-lg transition-colors" style={{ color: "#4A5450" }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "rgba(26,92,55,0.08)")}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}>
            <X size={20} weight="bold" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-6 py-6 space-y-5">
          {/* Name */}
          <div>
            <label className={labelCls} style={{ color: "#4A5450" }}>Name</label>
            <div className="relative">
              <User size={16} weight="duotone" className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "#7A8A84" }} />
              <input required type="text" placeholder="Full name" value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                className={inputCls} style={{ ...inputStyle, paddingLeft: "2.25rem" }}
                onFocus={(e) => (e.target.style.borderColor = "#1A5C37")}
                onBlur={(e) => (e.target.style.borderColor = "rgba(26,92,55,0.2)")} />
            </div>
          </div>

          {/* Email */}
          <div>
            <label className={labelCls} style={{ color: "#4A5450" }}>Email</label>
            <div className="relative">
              <Envelope size={16} weight="duotone" className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "#7A8A84" }} />
              <input required type="email" placeholder="admin@example.com" value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                className={inputCls} style={{ ...inputStyle, paddingLeft: "2.25rem" }}
                onFocus={(e) => (e.target.style.borderColor = "#1A5C37")}
                onBlur={(e) => (e.target.style.borderColor = "rgba(26,92,55,0.2)")} />
            </div>
          </div>

          {/* Password */}
          <div>
            <label className={labelCls} style={{ color: "#4A5450" }}>Password</label>
            <div className="relative">
              <Lock size={16} weight="duotone" className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "#7A8A84" }} />
              <input required type={showPassword ? "text" : "password"} placeholder="Min. 6 characters" value={form.password}
                onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                className={inputCls} style={{ ...inputStyle, paddingLeft: "2.25rem", paddingRight: "2.25rem" }}
                onFocus={(e) => (e.target.style.borderColor = "#1A5C37")}
                onBlur={(e) => (e.target.style.borderColor = "rgba(26,92,55,0.2)")} />
              <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2" style={{ color: "#7A8A84" }}>
                {showPassword ? <EyeSlash size={15} /> : <Eye size={15} />}
              </button>
            </div>
          </div>

          {/* Permissions */}
          <div className="space-y-3 pt-1">
            <p className={labelCls} style={{ color: "#4A5450" }}>Permissions</p>

            <label className="flex items-start gap-3 cursor-pointer select-none">
              <input
                type="checkbox" checked={form.allow_delete}
                onChange={(e) => setForm((f) => ({ ...f, allow_delete: e.target.checked }))}
                className="mt-0.5 w-4 h-4 rounded accent-green-700 cursor-pointer"
              />
              <div>
                <p className="text-sm font-medium" style={{ color: "#1C211F" }}>Allow Delete</p>
                <p className="text-xs" style={{ color: "#4A5450" }}>Can delete gallery images and reports. If unchecked, can only add.</p>
              </div>
            </label>

            <label className="flex items-start gap-3 cursor-pointer select-none">
              <input
                type="checkbox" checked={form.show_leads}
                onChange={(e) => setForm((f) => ({ ...f, show_leads: e.target.checked }))}
                className="mt-0.5 w-4 h-4 rounded accent-green-700 cursor-pointer"
              />
              <div>
                <p className="text-sm font-medium" style={{ color: "#1C211F" }}>Show Leads</p>
                <p className="text-xs" style={{ color: "#4A5450" }}>Grant access to the Leads page and submissions.</p>
              </div>
            </label>
          </div>

          {error && (
            <p className="text-xs px-3 py-2 rounded-lg" style={{ color: "#B85C38", backgroundColor: "rgba(184,92,56,0.08)" }}>{error}</p>
          )}
        </form>

        <div className="px-6 py-4 border-t flex gap-3 flex-shrink-0" style={{ borderColor: "rgba(26,92,55,0.1)" }}>
          <button type="button" onClick={() => setDrawerOpen(false)}
            className="flex-1 py-2.5 rounded-lg text-sm font-medium border transition-colors"
            style={{ borderColor: "rgba(26,92,55,0.2)", color: "#4A5450" }}>Cancel</button>
          <button onClick={handleSubmit} disabled={submitting}
            className="flex-1 py-2.5 rounded-lg text-sm font-medium transition-colors"
            style={{ backgroundColor: submitting ? "#4a8c62" : "#1A5C37", color: "#F7F5F0", cursor: submitting ? "not-allowed" : "pointer" }}>
            {submitting ? "Creating..." : "Create Admin"}
          </button>
        </div>
      </div>
    </div>
  );
}
